import type { TopLevelSpec } from 'vega-lite';

import type { JsonObject } from './vega-lite-json';
import { VEGA_LITE_DATASET_NAME } from './vega-lite-widget-options';

/**
 * A definition that has passed the Neptune policy and may cross into the Vega-Lite package typings.
 */
export type VegaLiteTopLevelSpec = JsonObject & TopLevelSpec;

/**
 * `TopLevelSpec` is a union no structural check can reproduce, so this guard is the single boundary
 * between Neptune's JSON model and the Vega-Lite package typings. It re-checks the two invariants
 * the policy guarantees - the fixed Neptune dataset binding and a bounded unit or layered shape -
 * so a definition can never reach Vega without having gone through `validateVegaLiteSpec` first.
 */
export function isVegaLiteTopLevelSpec(spec: JsonObject): spec is VegaLiteTopLevelSpec {
  const data = spec.data;
  const isNeptuneDataset =
    typeof data === 'object' &&
    data !== null &&
    !Array.isArray(data) &&
    data.name === VEGA_LITE_DATASET_NAME;

  if (!isNeptuneDataset) {
    return false;
  }

  return spec.mark !== undefined || Array.isArray(spec.layer);
}

export function toVegaLiteTopLevelSpec(spec: JsonObject): VegaLiteTopLevelSpec {
  if (!isVegaLiteTopLevelSpec(spec)) {
    throw new Error(
      `A Vega-Lite definition must be validated and bound to the "${VEGA_LITE_DATASET_NAME}" dataset before it is rendered.`,
    );
  }

  return spec;
}
